import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CHAPTERS, IMAGES } from "@/data/site";

const EASE = [0.76, 0, 0.24, 1];

const Reveal = ({ children, delay = 0 }) => (
    <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE, delay }}
    >
        {children}
    </motion.div>
);

export const Manifesto = () => {
    const imgRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: imgRef,
        offset: ["start end", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);

    return (
        <section
            id="historia"
            data-testid="historia-section"
            className="relative bg-[var(--paper)] text-[var(--ink)] py-24 md:py-32"
        >
            <div className="container-x">
                {/* Section header */}
                <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
                    <div className="col-span-12 md:col-span-4">
                        <div className="flex items-center gap-3 text-[11px] tracking-[0.32em] uppercase text-[var(--ink)]/60 font-body">
                            <span className="inline-block h-px w-10 bg-[var(--ink)]/40" />
                            <span>Seção 01</span>
                        </div>
                        <h2
                            data-testid="historia-title"
                            className="font-display text-[13vw] md:text-[7vw] lg:text-[6vw] mt-6"
                        >
                            Nossa <br />
                            <span className="font-editorial italic font-medium text-[var(--ink-2)]">
                                história.
                            </span>
                        </h2>
                    </div>

                    <div className="col-span-12 md:col-span-6 md:col-start-7 flex items-end">
                        <p className="font-editorial italic text-2xl md:text-3xl leading-tight text-[var(--ink)]/85 max-w-lg">
                            “Queremos ir além do futebol, formando não só bons
                            atletas, mas também bons cidadãos da sociedade
                            brasileira.”
                        </p>
                    </div>
                </div>

                {/* Chapters + portrait */}
                <div className="grid grid-cols-12 gap-6 md:gap-10">
                    {/* Portrait */}
                    <div className="col-span-12 md:col-span-5 lg:col-span-4">
                        <div className="sticky top-28">
                            <div
                                ref={imgRef}
                                className="photo-frame relative aspect-[3/4] w-full overflow-hidden bg-[var(--ink)]"
                            >
                                <motion.img
                                    style={{ y: imgY }}
                                    src={IMAGES.portrait}
                                    alt="Retrato de um beneficiado"
                                    className="absolute inset-0 h-[115%] w-full object-cover"
                                />
                                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-[10px] tracking-[0.32em] uppercase text-white/90 font-body">
                                    <span>Retrato · Novo Horizonte</span>
                                    <span>N°04</span>
                                </div>
                            </div>
                            <p className="mt-5 font-editorial italic text-[15px] text-[var(--ink)]/70 max-w-xs">
                                Cada criança que entra em campo carrega
                                consigo uma história — e a nossa começa em
                                cada uma delas.
                            </p>
                        </div>
                    </div>

                    {/* Chapters */}
                    <div className="col-span-12 md:col-span-7 lg:col-span-7 lg:col-start-6">
                        <div className="flex flex-col">
                            {CHAPTERS.map((c, i) => (
                                <Reveal key={c.n} delay={i * 0.05}>
                                    <article
                                        data-testid={`chapter-${c.n}`}
                                        className="grid grid-cols-12 gap-4 py-10 md:py-14 border-b border-[var(--ink)]/15 first:pt-0 last:border-0"
                                    >
                                        <div className="col-span-3 md:col-span-2">
                                            <div className="chapter-num text-[42px] md:text-[56px] leading-none text-[var(--ink)]/90">
                                                {c.n}
                                            </div>
                                            <div className="mt-2 text-[10px] tracking-[0.32em] uppercase text-[var(--ink)]/50 font-body">
                                                Cap.
                                            </div>
                                        </div>
                                        <div className="col-span-9 md:col-span-10">
                                            <div className="flex items-baseline gap-4 flex-wrap">
                                                <h3 className="font-editorial text-3xl md:text-4xl italic text-[var(--ink-2)]">
                                                    {c.title}
                                                </h3>
                                                <span className="font-display text-[11px] tracking-[0.32em] text-[var(--ink)]/50">
                                                    · {c.year}
                                                </span>
                                            </div>
                                            <p className="mt-4 font-body text-[15px] md:text-[16px] leading-relaxed text-[var(--ink)]/80 max-w-2xl">
                                                {c.body}
                                            </p>
                                        </div>
                                    </article>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
